import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import { MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrentUser, useNavigateToSignIn, useSignOut } from 'hooks'
import { TypeRoute } from 'core/TypeRoute'
import { useNavigate } from 'react-router-dom'

interface MenuItemInfo {
  id: string
  label: string
}

export const AvatarButton = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { currentUser } = useCurrentUser()
  const [isLoading, setIsLoading] = useState(false)

  const avatarName = useMemo(() => {
    const avatarName = currentUser?.firstName ?? currentUser?.email

    return avatarName?.substring(0, 2)?.toUpperCase()
  }, [currentUser])

  const menuItems: MenuItemInfo[] = useMemo(() => {
    if (currentUser) {
      return [
        {
          id: 'account',
          label: t('Account'),
        },
        {
          id: 'logout',
          label: t('Logout'),
        },
      ]
    }

    return [
      {
        id: 'login',
        label: t('Login'),
      },
    ]
  }, [t, currentUser])

  const signOut = useSignOut()
  const navigateToSignIn = useNavigateToSignIn()

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }
  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }
  const handleMenuItemClick = async (id: string) => {
    setAnchorElUser(null)

    switch (id) {
      case 'login':
        navigateToSignIn(TypeRoute.Home)
        break
      case 'account':
        navigate(TypeRoute.UserProfile)
        break
      case 'logout':
        setIsLoading(true)
        await signOut()
        setIsLoading(false)
        break
    }
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleOpenUserMenu}
          sx={{ p: 0 }}
          disabled={isLoading}
        >
          <Avatar>{avatarName}</Avatar>
        </IconButton>
        {isLoading && (
          <CircularProgress
            size={36}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-18px',
              marginLeft: '-18px',
            }}
          />
        )}
      </Box>
      <Menu
        sx={{ marginTop: 6 }}
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        {menuItems.map((mi) => (
          <MenuItem
            key={mi.id}
            onClick={() => handleMenuItemClick(mi.id)}
            disabled={isLoading}
          >
            {mi.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
